import { inferAssociatedEntity } from "@/lib/entity-graph"
import type { TranslationKey, TranslationVariables } from "@/lib/translations"
import type { AtsFieldDescriptor } from "@/types/ats-explorer-api"

/** The active language's translator, passed in because this module has no hooks */
export type Translate = (key: TranslationKey, variables?: TranslationVariables) => string

export type PresentedKind = "scalar" | "number" | "money" | "date" | "bool" | "html" | "assoc" | "list" | "empty"

export interface PresentedAssociation {
  /** The associated entity type when the schema names it (Bullhorn); null for inferred objects */
  entityType: string | null
  id: string
  title: string
}

export interface PresentedField {
  /** Dot path in the record (composite sub-fields are "address.city") */
  path: string
  label: string
  kind: PresentedKind
  raw: unknown
  /** Human-readable value */
  display: string
  /** Extra detail for the tooltip (the raw ISO/epoch behind a date, the tag-stripped source of HTML…) */
  detail?: string
  association?: PresentedAssociation
  list?: { total: number | null; items: unknown[] }
}

export interface PresentedGroup {
  key: string
  /** String key for the section heading; the record blade translates it */
  titleKey: TranslationKey
  fields: PresentedField[]
}

export interface PresentedRecord {
  id: string
  title: string
  status: string | null
  groups: PresentedGroup[]
  /** Every field including empties, in record order — the Fields tab and "show empty" toggle */
  all: PresentedField[]
}

export interface PresentOptions {
  /** The active language's translator, for the formatted values and group headings */
  t: Translate
  /** The entity's schema, for labels and association targets */
  schema?: AtsFieldDescriptor[] | null
  /** Fallback for the title when the record has no name/title */
  entityDisplayName?: string
  /** Entity names in the tenant's catalog; an embedded object the schema does not type is matched to one by key ("person" → "people") */
  entityNames?: string[]
}

const GROUPS: { key: string; titleKey: TranslationKey }[] = [
  { key: "identity", titleKey: "explorer.record.groups.identity" },
  { key: "dates", titleKey: "explorer.record.groups.dates" },
  { key: "people", titleKey: "explorer.record.groups.people" },
  { key: "lists", titleKey: "explorer.record.groups.lists" },
  { key: "money", titleKey: "explorer.record.groups.money" },
  { key: "other", titleKey: "explorer.record.groups.other" },
  { key: "custom", titleKey: "explorer.record.groups.custom" },
]

const IDENTITY_KEYS = new Set(["id", "name", "title", "subject", "firstName", "lastName", "status", "type", "email", "phone", "isDeleted", "isOpen"])
const DATE_KEY = /(date|_at$|At$|timestamp|time$|dateBegin|dateEnd|dateAdded|start|end|expir)/i
const MONEY_KEY = /(rate|salary|pay|bill|amount|fee|price|cost|margin|markup|commission|total|hours)/i
const HTML_VALUE = /<\/?[a-z][^>]*>/i
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/
const EPOCH_MS_MIN = 1e11

/** "dateLastModified" → "Date last modified", "person_global_status" → "Person global status", "customText3" → "Custom text 3" */
export function humanizeKey(key: string): string {
  const spaced = key
    .replace(/\[\]/g, "")
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([a-zA-Z])(\d+)$/, "$1 $2")
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true
  if (Array.isArray(value)) return value.length === 0
  if (isPlainObject(value)) {
    if ("total" in value && "data" in value) return Number(value.total) === 0
    return Object.keys(value).length === 0
  }
  return false
}

/** Bullhorn to-many payloads are `{ total, data: [...] }`; plain arrays are lists too */
function asList(value: unknown): { total: number | null; items: unknown[] } | null {
  if (Array.isArray(value)) return { total: value.length, items: value }
  if (isPlainObject(value) && Array.isArray(value.data) && ("total" in value || Object.keys(value).length === 1)) {
    return { total: typeof value.total === "number" ? value.total : value.data.length, items: value.data }
  }
  return null
}

export function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h\d)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

function formatDate(value: number | string): string {
  // A date without a time is a calendar date; parsing it as midnight UTC would shift it a day in western zones
  if (typeof value === "string" && DATE_ONLY.test(value)) {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { dateStyle: "medium" })
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

/** The best human handle for an object: name, title, subject, first + last name, else "#id" */
export function titleOf(record: Record<string, unknown>, fallback = ""): string {
  const direct = ["name", "title", "subject", "label"].map((key) => record[key]).find((v) => typeof v === "string" && v.trim() !== "")
  if (typeof direct === "string") return direct
  const first = typeof record.firstName === "string" ? record.firstName : typeof record.first_name === "string" ? record.first_name : ""
  const last = typeof record.lastName === "string" ? record.lastName : typeof record.last_name === "string" ? record.last_name : ""
  const full = `${first} ${last}`.trim()
  if (full) return full
  if (record.id !== undefined && record.id !== null) return `${fallback ? `${fallback} ` : ""}#${String(record.id)}`
  return fallback
}

const ID_KEY = /(^|\.|_)id$|Id$/

/** One scalar as the reader should see it: dates as dates, money grouped, ids left alone */
export function displayScalar(path: string, value: unknown, t: Translate, schemaType?: string): string {
  return scalarField(path, path, value, t, schemaType).display
}

function scalarField(path: string, label: string, value: unknown, t: Translate, schemaType?: string): PresentedField {
  if (typeof value === "boolean") return { path, label, kind: "bool", raw: value, display: t(value ? "common.state.yes" : "common.state.no") }
  if (typeof value === "number") {
    // Identifiers are numbers in Bullhorn and Loxo but must never be grouped with separators
    if (ID_KEY.test(path)) return { path, label, kind: "scalar", raw: value, display: String(value) }
    if (DATE_KEY.test(path) && value > EPOCH_MS_MIN) {
      return { path, label, kind: "date", raw: value, display: formatDate(value), detail: t("explorer.record.epochMs", { value }) }
    }
    const money = MONEY_KEY.test(path)
    return { path, label, kind: money ? "money" : "number", raw: value, display: value.toLocaleString(undefined, { maximumFractionDigits: 4 }) }
  }
  const text = String(value)
  if (ISO_DATE.test(text) || (schemaType === "datetime" && !Number.isNaN(new Date(text).getTime()))) {
    return { path, label, kind: "date", raw: value, display: formatDate(text), detail: text }
  }
  if (HTML_VALUE.test(text)) {
    return { path, label, kind: "html", raw: value, display: stripHtml(text), detail: t("explorer.record.htmlStripped") }
  }
  return { path, label, kind: "scalar", raw: value, display: text }
}

function describeValue(path: string, label: string, value: unknown, descriptor: AtsFieldDescriptor | undefined, entityNames: string[], t: Translate): PresentedField[] {
  if (isEmptyValue(value)) return [{ path, label, kind: "empty", raw: value, display: "" }]

  const list = asList(value)
  if (list) {
    const preview = list.items.slice(0, 3).map((item) => (isPlainObject(item) ? titleOf(item) : String(item))).filter(Boolean)
    const total = list.total ?? list.items.length
    const counted = t((total === 1 ? "explorer.record.itemsOne" : "explorer.record.itemsOther"), { count: total })
    return [{ path, label, kind: "list", raw: value, display: `${counted}${preview.length ? ` · ${preview.join(", ")}` : ""}`, list }]
  }

  if (isPlainObject(value)) {
    const rawId = embeddedId(path, value)
    if (rawId !== undefined) {
      const id = String(rawId)
      const title = titleOf(value)
      return [{
        path,
        label,
        kind: "assoc",
        raw: value,
        display: title === `#${id}` ? `#${id}` : `${title} · #${id}`,
        association: { entityType: descriptor?.associatedEntityType ?? inferAssociatedEntity(`${path}.id`, entityNames), id, title },
      }]
    }
    // Composite (address, compensation…) — flatten one level
    return Object.entries(value).flatMap(([sub, subValue]) => describeValue(`${path}.${sub}`, `${label} ${humanizeKey(sub).toLowerCase()}`, subValue, undefined, entityNames, t))
  }

  return [scalarField(path, label, value, t, descriptor?.dataType)]
}

/** The identifier inside an embedded object: "id", or JobAdder's "{key}Id" ("candidate" → candidateId) */
function embeddedId(path: string, value: Record<string, unknown>): unknown {
  if (value.id !== undefined && value.id !== null) return value.id
  const ownIdKey = `${path.split(".").pop()}id`.toLowerCase()
  const key = Object.keys(value).find((k) => k.toLowerCase() === ownIdKey)
  return key !== undefined && value[key] !== null ? value[key] : undefined
}

function groupKeyFor(field: PresentedField, topKey: string): string {
  if (/^(custom|correlatedCustom)/i.test(topKey)) return "custom"
  if (field.kind === "assoc") return "people"
  if (field.kind === "list") return "lists"
  if (field.kind === "date") return "dates"
  if (field.kind === "money") return "money"
  if (IDENTITY_KEYS.has(topKey)) return "identity"
  return "other"
}

/**
 * Turns a raw vendor record into labelled, grouped, formatted fields for the record
 * blade. Labels come from the schema when it has them (Bullhorn meta labels), else
 * from the key; kinds come from the value shape and key hints, so Loxo and JobAdder
 * records read well without metadata.
 */
export function presentRecord(record: Record<string, unknown>, options: PresentOptions): PresentedRecord {
  const byPath = new Map((options.schema ?? []).map((field) => [field.path, field] as const))
  const all: PresentedField[] = []
  const buckets = new Map<string, PresentedField[]>()

  for (const [key, value] of Object.entries(record)) {
    if (key.startsWith("_")) continue
    const descriptor = byPath.get(key) ?? byPath.get(`${key}[]`)
    const label = descriptor?.label?.trim() || humanizeKey(key)
    for (const field of describeValue(key, label, value, descriptor, options.entityNames ?? [], options.t)) {
      all.push(field)
      if (field.kind === "empty") continue
      const groupKey = groupKeyFor(field, key)
      const bucket = buckets.get(groupKey)
      if (bucket) bucket.push(field)
      else buckets.set(groupKey, [field])
    }
  }

  const status = typeof record.status === "string" ? record.status : null
  return {
    id: record.id === undefined || record.id === null ? "" : String(record.id),
    title: titleOf(record, options.entityDisplayName),
    status,
    groups: GROUPS.filter((group) => buckets.has(group.key)).map((group) => ({ ...group, fields: buckets.get(group.key)! })),
    all,
  }
}

export interface LeafEntry {
  /** SourceField notation: "parent.child", "items[].child" */
  path: string
  raw: unknown
  display: string
}

/** Every leaf of a record in SourceField notation, for the Fields tab */
export function flattenLeaves(value: unknown, t: Translate, path = ""): LeafEntry[] {
  if (Array.isArray(value)) {
    if (value.length === 0) return [{ path, raw: value, display: "" }]
    // Arrays are described by their first element's shape — the path uses [] like the mapping engine
    return value.slice(0, 1).flatMap((item) => flattenLeaves(item, t, `${path}[]`))
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value)
    if (entries.length === 0) return [{ path, raw: value, display: "" }]
    return entries.flatMap(([key, child]) => flattenLeaves(child, t, path ? `${path}.${key}` : key))
  }
  if (value === null || value === undefined) return [{ path, raw: value, display: "" }]
  return [{ path, raw: value, display: scalarField(path, path, value, t).display }]
}
